import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
	createCompany,
	softDeleteCompany,
	restoreCompany,
	bulkDeleteCompanies,
	bulkRestoreCompanies,
} from '../../api/crmApi';
import { dealsKeys } from '../deals/keys';

import { companiesKeys, companiesMutationKeys } from './keys';

export function useCreateCompany() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: companiesMutationKeys.create,
		mutationFn: createCompany,
		onSettled: () => {
			qc.invalidateQueries({ queryKey: companiesKeys.all });
		},
	});
}

export function useSoftDeleteCompany() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: companiesMutationKeys.softDelete,
		mutationFn: softDeleteCompany,
		// Invalidate companies + deal lists/summaries (deals reference company)
		onSettled: (data, err, companyId) => {
			qc.invalidateQueries({ queryKey: companiesKeys.all });
			qc.invalidateQueries({ queryKey: ['crm', 'company', companyId] });
			qc.invalidateQueries({ queryKey: dealsKeys.all });
		},
	});
}

export function useRestoreCompany() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: companiesMutationKeys.restore,
		mutationFn: restoreCompany,
		// Invalidate companies + deal lists/summaries to reflect restored state
		onSettled: (data, err, companyId) => {
			qc.invalidateQueries({ queryKey: companiesKeys.all });
			qc.invalidateQueries({ queryKey: ['crm', 'company', companyId] });
			qc.invalidateQueries({ queryKey: dealsKeys.all });
		},
	});
}

function invalidateAfterBulkCompanyChange(qc) {
	qc.invalidateQueries({ queryKey: companiesKeys.all });
	qc.invalidateQueries({ queryKey: ['crm', 'company'] });
	qc.invalidateQueries({ queryKey: dealsKeys.all });
}

export function useBulkDeleteCompanies() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: companiesMutationKeys.bulkDelete,
		mutationFn: bulkDeleteCompanies,
		onSettled: () => invalidateAfterBulkCompanyChange(qc),
	});
}

export function useBulkRestoreCompanies() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: companiesMutationKeys.bulkRestore,
		mutationFn: bulkRestoreCompanies,
		onSettled: () => invalidateAfterBulkCompanyChange(qc),
	});
}
